import { useState, useCallback, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { ROLES } from "../../uitls/Utils";
import {
  mfaSetup,
  mfaVerifyAndEnable,
  mfaLoginWithTotp,
} from "../../api/userApi";
import { setUser } from "../../features/user/userSlice";
import {
  Security as SecurityIcon,
  ContentCopy as ContentCopyIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";

const getDashboardRoute = (roleId) => {
  switch (roleId) {
    case ROLES.admin:
      return "/admin/dashboard";
    case ROLES.mgr:
      return "/mgr/dashboard";
    case ROLES.hr:
      return "/hr/dashboard";
    case ROLES.emp:
      return "/emp/dashboard";
    default:
      return "/emp/dashboard";
  }
};

const MfaSetup = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    corporateEmail: emailFromState,
    password,
    mfaEnabled,
    roleId: roleIdFromState,
    secret,
    qrCodeUrl,
    manualEntryKey,
  } = location.state || {};

  const getEmail = () => {
    if (emailFromState) return emailFromState;
    
    try {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        const user = JSON.parse(storedUser);
        return user?.corporateEmail || user?.email || null;
      }
    } catch (error) {
      console.error("Error parsing user:", error);
    }
    return null;
  };

  const corporateEmail = getEmail();
  const roleId = roleIdFromState || localStorage.getItem("roleId");

  const [mfaData, setMfaData] = useState(null);
  const [totpCode, setTotpCode] = useState("");
  const [totpError, setTotpError] = useState("");
  const [copySuccess, setCopySuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingMfaData, setIsLoadingMfaData] = useState(false);
  const hasRedirectedRef = useRef(false);

  useEffect(() => {
    if (hasRedirectedRef.current) return;
    
    const mfaRequiredStored = localStorage.getItem("mfaRequired") === 'true';
    
    if (!corporateEmail) {
      console.log("❌ No email found - redirecting to sign in");
      hasRedirectedRef.current = true;
      
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("roleId");
      localStorage.removeItem("mfaVerified");
      localStorage.removeItem("mfaRequired");
      
      navigate("/signIn", { 
        replace: true,
        state: { message: "Please sign in to continue" }
      });
      return;
    }
    
    if (mfaRequiredStored && !password) {
      console.log("❌ MFA verification requires fresh login (no password) - redirecting to sign in");
      hasRedirectedRef.current = true;
      
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("roleId");
      localStorage.removeItem("mfaVerified");
      localStorage.removeItem("mfaRequired");
      
      navigate("/signIn", { 
        replace: true,
        state: { message: "Please sign in again to complete MFA verification" }
      });
    }
  }, [corporateEmail, password, navigate]);

  useEffect(() => {
    if (!corporateEmail) {
      return;
    }

    if (mfaEnabled !== true) {
      if (qrCodeUrl && secret) {
        setMfaData({
          qrCodeUrl: qrCodeUrl,
          secretKey: secret,
          manualEntryKey: manualEntryKey || secret,
        });
        setIsLoadingMfaData(false);
      } else {
        setIsLoadingMfaData(true);
        dispatch(mfaSetup(corporateEmail))
          .unwrap()
          .then((data) => {
            setMfaData(data);
            setIsLoadingMfaData(false);
          })
          .catch((error) => {
            console.error("❌ MFA setup error:", error);
            setTotpError("Failed to load MFA setup. Please try again.");
            setIsLoadingMfaData(false);
          });
      }
    }
  }, [corporateEmail, mfaEnabled, qrCodeUrl, secret, manualEntryKey, dispatch]);

  const handleTotpChange = useCallback(
    (e) => {
      const value = e.target.value.replace(/\D/g, "").slice(0, 6);
      setTotpCode(value);
      if (totpError) {
        setTotpError("");
      }
    },
    [totpError]
  );

  const copyToClipboard = useCallback((text) => {
    navigator.clipboard.writeText(text);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  }, []);

const handleMfaSetupVerify = useCallback(async () => {
  if (!totpCode || totpCode.length !== 6) {
    setTotpError("Please enter a valid 6-digit code");
    return;
  }

  setIsSubmitting(true);

  try {
    const result = await dispatch(
      mfaVerifyAndEnable({
        corporateEmail,
        token: totpCode,
      })
    ).unwrap();

    if (result && result.status === "success") {
      const user = result?.data?.user;
      const token = result?.data?.token;
      const userRoleId = user?.userRoleId || roleId;

      console.log("✅ MFA Setup Successful - Token:", token ? "Present" : "Missing");

      if (!token) {
        setTotpError("Authentication failed. Please try again.");
        return;
      }

      // Update Redux store with verified MFA status
      dispatch(setUser({
        token: token,
        user: user,
        roleId: userRoleId,
        mfaRequired: true,
        mfaVerified: true
      }));

      // Navigate to dashboard
      const dashboardRoute = getDashboardRoute(userRoleId);
      navigate(dashboardRoute, { replace: true });
    } else {
      setTotpError("Invalid verification code. Please try again.");
    }
  } catch (error) {
    setTotpError("Invalid verification code. Please try again.");
    console.error("MFA setup verification error:", error);
  } finally {
    setIsSubmitting(false);
  }
}, [totpCode, corporateEmail, dispatch, navigate, roleId]);

const handleMfaVerify = useCallback(async () => {
  if (!totpCode || totpCode.length !== 6) {
    setTotpError("Please enter a valid 6-digit code");
    return;
  }

  if (!password) {
    setTotpError("Password is required. Please go back and login again.");
    return;
  }

  setIsSubmitting(true);

  try {
    const result = await dispatch(
      mfaLoginWithTotp({
        corporateEmail,
        password,
        totpCode,
      })
    ).unwrap();

    if (result && result.status === "success") {
      // Successfully logged in with MFA - update Redux state
      const user = result.user;
      const token = result.token;
      const userRoleId = result.roleId;

      console.log("✅ MFA Login Successful - Updating state");

      // Update Redux store with verified MFA status
      dispatch(setUser({
        token: token,
        user: user,
        roleId: userRoleId,
        mfaRequired: true,
        mfaVerified: true
      }));

      // Navigate to dashboard
      const dashboardRoute = getDashboardRoute(userRoleId);
      navigate(dashboardRoute, { replace: true });
    } else {
      setTotpError("Invalid verification code. Please try again.");
    }
  } catch (error) {
    setTotpError("Invalid verification code. Please try again.");
    console.error("MFA verification error:", error);
  } finally {
    setIsSubmitting(false);
  }
}, [totpCode, corporateEmail, password, dispatch, navigate]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter") {
        if (mfaEnabled !== true) {
          handleMfaSetupVerify();
        } else {
          handleMfaVerify();
        }
      }
    },
    [handleMfaSetupVerify, handleMfaVerify, mfaEnabled]
  );

  const handleBackToLogin = () => {
    navigate("/signIn", { replace: true });
  };

  // Render MFA Setup (First Time)
  const renderMfaSetup = () => (
    <div className="w-full max-w-md">
      {/* Title */}
      <div className="text-center mb-6 md:mb-8">
        <div className="flex justify-center mb-4">
          <div className="p-4 bg-blue-100 rounded-full">
            <SecurityIcon className="h-12 w-12 md:h-16 md:w-16 text-blue-600" />
          </div>
        </div>
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
          Setup Two-Factor Authentication
        </h2>
        <p className="text-sm md:text-base text-gray-600">
          Scan the QR code below with your authenticator app to get started
        </p>
      </div>

      {/* Loading State */}
      {isLoadingMfaData && (
        <div className="mb-6 md:mb-8 flex flex-col items-center justify-center py-12">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent mb-4"></div>
          <p className="text-gray-600 text-sm">Loading QR code...</p>
        </div>
      )}

      {/* QR Code Display */}
      {!isLoadingMfaData && mfaData?.qrCodeUrl && (
        <div className="mb-6 md:mb-8">
          <div className="bg-white p-6 rounded-xl border-2 border-gray-200 shadow-sm flex justify-center">
            <img
              src={mfaData.qrCodeUrl}
              alt="QR Code for MFA Setup"
              className="w-56 h-56 md:w-64 md:h-64"
            />
          </div>
          <p className="mt-3 text-xs md:text-sm text-gray-500 text-center">
            Use Google Authenticator, Authy, or any TOTP app
          </p>
        </div>
      )}

      {/* Secret Key Display */}
      {!isLoadingMfaData && mfaData?.secretKey && (
        <div className="mb-6 md:mb-8">
          <label
            htmlFor="secret-key-input"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Manual Entry Key
          </label>
          <div className="flex items-center gap-2">
            <input
              id="secret-key-input"
              type="text"
              value={mfaData.secretKey}
              readOnly
              className="flex-1 px-3 py-3 border border-gray-300 rounded-lg bg-gray-50 text-sm md:text-base font-mono text-gray-700"
            />
            <button
              onClick={() => copyToClipboard(mfaData.secretKey)}
              className="p-3 brand-btn-primary rounded-lg transition-colors"
              title="Copy to clipboard"
            >
              <ContentCopyIcon className="h-5 w-5" />
            </button>
          </div>
          {copySuccess && (
            <p className="mt-2 text-sm text-green-600 flex items-center">
              ✓ Copied to clipboard!
            </p>
          )}
          <p className="mt-2 text-xs text-gray-500">
            Can't scan the QR code? Enter this key manually in your
            authenticator app
          </p>
        </div>
      )}

      {/* TOTP Input and Verify Button */}
      {!isLoadingMfaData && (
        <>
          <div className="mb-6 md:mb-8">
            <label
              htmlFor="totp-setup-input"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Enter Verification Code
            </label>
            <input
              id="totp-setup-input"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              placeholder="000000"
              value={totpCode}
              onChange={handleTotpChange}
              onKeyDown={handleKeyDown}
              autoFocus
              className="block w-full px-4 py-4 border border-gray-300 rounded-lg text-center text-2xl md:text-3xl tracking-widest font-mono focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            />
            {totpError && (
              <p className="mt-2 text-sm text-red-600">{totpError}</p>
            )}
            <p className="mt-2 text-xs text-gray-500">
              Enter the 6-digit code from your authenticator app
            </p>
          </div>

          {/* Verify Button */}
          <button
            onClick={handleMfaSetupVerify}
            disabled={isSubmitting || totpCode.length !== 6}
            className="w-full py-4 px-4 brand-btn-primary rounded-lg shadow-md transition-all duration-200 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed text-base md:text-lg ripple"
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                <span>Verifying...</span>
              </div>
            ) : (
              "Verify and Enable MFA"
            )}
          </button>
        </>
      )}

      {/* Back to Login */}
      <div className="mt-6 text-center">
        <button
          onClick={handleBackToLogin}
          className="text-sm text-white/90 hover:text-white inline-flex items-center gap-1"
        >
          <ArrowBackIcon className="h-4 w-4" />
          Back to Login
        </button>
      </div>
    </div>
  );

  // Render MFA Verification (Returning User)
  const renderMfaVerify = () => (
    <div className="w-full max-w-md">
      {/* Title */}
      <div className="text-center mb-6 md:mb-8">
        <div className="flex justify-center mb-4">
          <div className="p-4 bg-blue-100 rounded-full">
            <SecurityIcon className="h-12 w-12 md:h-16 md:w-16 text-blue-600" />
          </div>
        </div>
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
          Two-Factor Authentication
        </h2>
        <p className="text-sm md:text-base text-gray-600">
          Enter the verification code from your authenticator app
        </p>
      </div>

      {/* TOTP Input */}
      <div className="mb-8 md:mb-10">
        <label
          htmlFor="totp-verify-input"
          className="block text-sm font-medium text-gray-700 mb-2"
        >
          Verification Code
        </label>
        <input
          id="totp-verify-input"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={6}
          placeholder="000000"
          value={totpCode}
          onChange={handleTotpChange}
          onKeyDown={handleKeyDown}
          autoFocus
          className="block w-full px-4 py-4 border border-gray-300 rounded-lg text-center text-2xl md:text-3xl tracking-widest font-mono focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
        />
        {totpError && <p className="mt-2 text-sm text-red-600">{totpError}</p>}
        <p className="mt-2 text-xs text-gray-500">
          Enter the 6-digit code from your authenticator app
        </p>
      </div>

      {/* Verify Button */}
      <button
        onClick={handleMfaVerify}
        disabled={isSubmitting || totpCode.length !== 6}
        className="w-full py-4 px-4 brand-btn-primary rounded-lg shadow-md transition-all duration-200 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed text-base md:text-lg ripple"
      >
        {isSubmitting ? (
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
            <span>Verifying...</span>
          </div>
        ) : (
          "Verify Code"
        )}
      </button>

      {/* Back to Login */}
      <div className="mt-6 text-center">
        <button
          onClick={handleBackToLogin}
          className="text-sm text-white/90 hover:text-white inline-flex items-center gap-1"
        >
          <ArrowBackIcon className="h-4 w-4" />
          Back to Login
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-[100dvh] brand-header flex items-start justify-center p-4 md:p-8">
      {/* Main Container */}
      <div className="w-full max-w-6xl bg-white rounded-2xl md:rounded-3xl shadow-2xl  relative">
        <div className="flex flex-col lg:flex-row max-h-[100vh] overflow-y-auto overflow-x-hidden scrollbar-hide">
          {/* Form Section - Left */}
          <div className="w-full lg:w-1/2 flex items-center justify-center p-6 md:p-8 lg:p-16">
            {mfaEnabled !== true ? renderMfaSetup() : renderMfaVerify()}
          </div>

          {/* Blue Design Section - Right - Hidden on Mobile */}
          <div className="hidden lg:block w-1/2 relative ">
            {/* Large Blue Abstract Shape */}
            <div className="absolute top-1/2 right-0 transform translate-x-1/2 -translate-y-1/2 w-[180%] h-[122%] brand-sidebar rounded-full"></div>

            {/* White Text Overlay */}
            <div className="relative z-10 h-full flex flex-col justify-center items-center px-16">
              <div className="text-white text-center max-w-md">
                <h1 className="text-5xl font-bold mb-4 tracking-wide ml-7">
                  Secure Your Account
                </h1>
                <p className="text-xl leading-relaxed text-left mb-8 ml-15">
                  Two-factor authentication adds an extra layer of security to
                  protect your data
                </p>
                <div className="space-y-3 text-left ml-15">
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-blue-300 rounded-full mr-3"></div>
                    <span className="text-lg">Enhanced Security</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-blue-300 rounded-full mr-3"></div>
                    <span className="text-lg">Protect Your Data</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-blue-300 rounded-full mr-3"></div>
                    <span className="text-lg">Easy Setup Process</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-blue-300 rounded-full mr-3"></div>
                    <span className="text-lg">Always Protected</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Small Blue Spheres - Outside Abstract Shape - Hidden on Mobile */}
        <div className="hidden lg:block absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-12 z-20 ml-4">
          <div className="w-16 h-16 brand-sidebar rounded-full"></div>
          <div className="w-12 h-12 brand-sidebar rounded-full"></div>
        </div>
      </div>
    </div>
  );
};

export default MfaSetup;